import { useDraggable } from '@dnd-kit/core';
import cn from 'classnames';
import { FC, ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { Button, Icon } from '@/shared/ui';

import {
  PALETTE_DRAG_TYPES,
  PaletteDragType,
  templateEditorMessages,
} from '../../config';
import { EditorButton } from '../EditorButton';

import styles from './EditorToolbar.module.scss';

type EditorToolbarProps = {
  allowOptions: boolean;
  hasSections: boolean;
  hasAnyOptions: boolean;
  canUndo: boolean;
  canRedo: boolean;
  className?: string;
  onAddSection: () => void;
  onAddStrictOption: () => void;
  onAddField: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onAddFieldPointerDownCapture?: () => void;
};

type DraggableToolbarButtonProps = {
  dragType: PaletteDragType;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  keepsEditorFocus?: boolean;
  onClick: () => void;
  onPointerDownCapture?: () => void;
};

const DraggableToolbarButton: FC<DraggableToolbarButtonProps> = (props) => {
  const {
    dragType,
    children,
    className,
    disabled,
    keepsEditorFocus,
    onClick,
    onPointerDownCapture,
  } = props;

  const { listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: dragType,
    disabled,
  });

  return (
    <Button
      ref={setNodeRef}
      variant="secondary"
      size="medium"
      icon="plus"
      disabled={disabled}
      className={cn(
        styles.dragButton,
        isDragging && styles.dragging,
        className,
      )}
      style={
        transform
          ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
          : undefined
      }
      onPointerDownCapture={onPointerDownCapture}
      // A focused option editor drops its caret insertion target on blur, so
      // the button must not take focus away from it.
      onMouseDown={(event) => keepsEditorFocus && event.preventDefault()}
      // Only the pointer starts a drag: the keyboard listeners would turn
      // Enter/Space into a keyboard drag and take the click insertion away
      // from keyboard users.
      onPointerDown={(event) => listeners?.onPointerDown?.(event)}
      onClick={onClick}
    >
      {children}
    </Button>
  );
};

export const EditorToolbar: FC<EditorToolbarProps> = (props) => {
  const {
    allowOptions,
    hasSections,
    hasAnyOptions,
    canUndo,
    canRedo,
    className,
    onAddSection,
    onAddStrictOption,
    onAddField,
    onUndo,
    onRedo,
    onAddFieldPointerDownCapture,
  } = props;

  const { formatMessage } = useIntl();

  return (
    <div
      className={cn(styles.container, className)}
      data-component="template-editor-toolbar"
    >
      <div className={styles.group}>
        <DraggableToolbarButton
          dragType={PALETTE_DRAG_TYPES.SECTION}
          onClick={onAddSection}
        >
          {formatMessage(templateEditorMessages.addSection)}
        </DraggableToolbarButton>
        {allowOptions && (
          <>
            <DraggableToolbarButton
              dragType={PALETTE_DRAG_TYPES.OPTION}
              disabled={!hasSections}
              onClick={onAddStrictOption}
            >
              {formatMessage(templateEditorMessages.addStrictOption)}
            </DraggableToolbarButton>
            <DraggableToolbarButton
              dragType={PALETTE_DRAG_TYPES.FIELD}
              disabled={!hasAnyOptions}
              keepsEditorFocus
              onClick={onAddField}
              onPointerDownCapture={onAddFieldPointerDownCapture}
            >
              {formatMessage(templateEditorMessages.addSlot)}
            </DraggableToolbarButton>
          </>
        )}
      </div>
      <div className={styles.group}>
        <EditorButton
          variant="icon"
          className={styles.historyButton}
          title={formatMessage(templateEditorMessages.undoButton)}
          disabled={!canUndo}
          onClick={onUndo}
        >
          <Icon name="editorUndo" size={22} />
        </EditorButton>
        <EditorButton
          variant="icon"
          className={styles.historyButton}
          title={formatMessage(templateEditorMessages.redoButton)}
          disabled={!canRedo}
          onClick={onRedo}
        >
          <Icon name="editorRedo" size={22} />
        </EditorButton>
      </div>
    </div>
  );
};
